import type { ActivationRunRecord, ActivationRunStatus, CreateActivationRunArgs } from "./types";

const ACTIVATION_RUNS_STORAGE_KEY = "libretto:activation:runs:v1";
const ACTIVATION_RUN_TTL_MS = 7 * 24 * 60 * 60 * 1000;

type ActivationRunsStore = Record<string, ActivationRunRecord>;

const isBrowser = () =>
    typeof window !== "undefined" && typeof window.sessionStorage !== "undefined";

const nowMs = () => Date.now();

const makeRunId = (): string => {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    return `activation_${nowMs()}_${Math.random().toString(36).slice(2, 10)}`;
};

const readStore = (): ActivationRunsStore => {
    if (!isBrowser()) return {};
    try {
        const raw = window.sessionStorage.getItem(ACTIVATION_RUNS_STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw) as ActivationRunsStore;
        if (!parsed || typeof parsed !== "object") return {};
        return parsed;
    } catch {
        return {};
    }
};

const writeStore = (store: ActivationRunsStore) => {
    if (!isBrowser()) return;
    try {
        window.sessionStorage.setItem(ACTIVATION_RUNS_STORAGE_KEY, JSON.stringify(store));
    } catch {}
};

const pruneExpiredRuns = (store: ActivationRunsStore): ActivationRunsStore => {
    const cutoff = nowMs() - ACTIVATION_RUN_TTL_MS;
    const entries = Object.entries(store).filter(([, record]) => {
        const updatedAt = Number(record?.updatedAt ?? 0);
        return Number.isFinite(updatedAt) && updatedAt >= cutoff;
    });
    return Object.fromEntries(entries);
};

const updateRun = (
    runId: string,
    updater: (run: ActivationRunRecord) => ActivationRunRecord
): ActivationRunRecord | null => {
    if (!runId) return null;
    const store = pruneExpiredRuns(readStore());
    const existing = store[runId];
    if (!existing) {
        writeStore(store);
        return null;
    }
    const next = updater(existing);
    store[runId] = {
        ...next,
        id: existing.id,
        updatedAt: nowMs(),
    };
    writeStore(store);
    return store[runId];
};

export const createActivationRun = (args: CreateActivationRunArgs): ActivationRunRecord | null => {
    if (!args?.flowId) return null;
    const runId = makeRunId();
    const record: ActivationRunRecord = {
        id: runId,
        flowId: args.flowId,
        status: "pending",
        currentStepIndex: 0,
        createdAt: nowMs(),
        updatedAt: nowMs(),
        source: args.source,
        projectId: args.projectId,
    };

    const store = pruneExpiredRuns(readStore());
    store[runId] = record;
    writeStore(store);
    return record;
};

export const getActivationRun = (runId: string | null | undefined): ActivationRunRecord | null => {
    const safeRunId = String(runId || "").trim();
    if (!safeRunId) return null;
    const store = pruneExpiredRuns(readStore());
    writeStore(store);
    return store[safeRunId] ?? null;
};

export const markActivationRunRunning = (runId: string): ActivationRunRecord | null =>
    updateRun(runId, (run) => ({ ...run, status: "running" }));

export const setActivationRunStepIndex = (
    runId: string,
    stepIndex: number
): ActivationRunRecord | null => {
    const safeStep = Number.isFinite(stepIndex) ? Math.max(0, Math.floor(stepIndex)) : 0;
    return updateRun(runId, (run) => ({ ...run, currentStepIndex: safeStep }));
};

export const finishActivationRun = (
    runId: string,
    status: ActivationRunStatus
): ActivationRunRecord | null =>
    updateRun(runId, (run) => ({ ...run, status }));

